import React, { useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, TextField,
  Switch, FormControlLabel, Stack, Alert, Divider, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  Tooltip, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import { useHistory } from 'react-router-dom';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { KS_BRACKETS } from '../../utils/fiscalPolicy';
import { validateBrackets, addBracketAfterLast, removeBracketReconnect, describeBrackets, sortBrackets } from '../../utils/fiscalBrackets';

// Shkallët e ligjit në fuqi — një burim i vetëm (`utils/fiscalPolicy.js`).
// Këtu rrinin ende ato të vjetrat (0–80 / 80–250 4%), dhe butoni «Ngarko normat
// KS» i kthente te çdo politikë e re.
const KS_DEFAULT_BRACKETS = KS_BRACKETS.map((b) => ({ From: b.From, To: b.To, Tax: b.Tax, desc: b.Description || '' }));

function fmtEur(n) {
  if (n === null || n === undefined || n === '') return 'Pa kufi';
  return `${Number(n).toLocaleString('sq-AL')} €`;
}

export default function AddFiscalPolitic() {
  const history = useHistory();
  const today   = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    Name:                 'Politika Fiskale ' + new Date().getFullYear(),
    ValidFrom:            today,
    ValidTo:              '',
    Active:               true,
    PensionEmployee:      5,
    PensionEmployer:      5,
    MinimumWage:          264,
    MaxOvertimeHoursWeek: 8,
    OvertimeRate:         30,
    NightWorkRate:        30,
    WeekendRate:          50,
    HolidayRate:          50,
    OnCallRate:           20,
  });

  const [brackets, setBrackets] = useState(
    KS_DEFAULT_BRACKETS.map((b, i) => ({ ...b, id: i + 1 }))
  );

  const [errors, setErrors]   = useState({});
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [toast, setToast]     = useState('');

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const setBracketField = (id, field, val) => {
    setBrackets(prev => prev.map(b =>
      b.id === id ? { ...b, [field]: val === '' ? null : Number(val) } : b
    ));
  };

  const addBracket = () => {
    const maxId = Math.max(...brackets.map(b => b.id), 0);
    setBrackets(prev => addBracketAfterLast(prev, () => maxId + 1));
  };

  const removeBracket = (id) => {
    if (brackets.length <= 1) return;
    setBrackets(prev => removeBracketReconnect(prev, b => b.id === id));
  };

  const loadKSDefaults = () => {
    setBrackets(KS_DEFAULT_BRACKETS.map((b, i) => ({ ...b, id: i + 1 })));
    setForm(prev => ({
      ...prev,
      PensionEmployee: 5, PensionEmployer: 5,
      MaxOvertimeHoursWeek: 8,
      OvertimeRate: 30, NightWorkRate: 30, WeekendRate: 50, HolidayRate: 50, OnCallRate: 20,
    }));
    setToast('Normat e Kosovës u ngarkuan!');
    setTimeout(() => setToast(''), 2500);
  };

  const validate = () => {
    const e = {};
    if (!form.Name.trim())     e.Name      = 'Emri është i detyrueshëm';
    if (!form.ValidFrom)       e.ValidFrom = 'Data e fillimit është e detyrueshme';
    if (Number(form.PensionEmployee) < 0 || Number(form.PensionEmployee) > 100)
      e.PensionEmployee = 'Vlera 0–100%';
    if (Number(form.PensionEmployer) < 0 || Number(form.PensionEmployer) > 100)
      e.PensionEmployer = 'Vlera 0–100%';
    if (Number(form.MinimumWage) < 0)
      e.MinimumWage = 'Paga minimale nuk mund të jetë negative';
    const bErr = validateBrackets(brackets);
    if (bErr) e._brackets = bErr;
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const policy = {
        Name:                 form.Name,
        ValidFrom:            form.ValidFrom,
        ValidTo:              form.ValidTo || null,
        Active:               form.Active,
        PensionEmployee:      Number(form.PensionEmployee),
        PensionEmployer:      Number(form.PensionEmployer),
        MinimumWage:          Number(form.MinimumWage),
        MaxOvertimeHoursWeek: Number(form.MaxOvertimeHoursWeek),
        OvertimeRate:         Number(form.OvertimeRate),
        NightWorkRate:        Number(form.NightWorkRate),
        WeekendRate:          Number(form.WeekendRate),
        HolidayRate:          Number(form.HolidayRate),
        OnCallRate:           Number(form.OnCallRate),
        Year:                 new Date(form.ValidFrom).getFullYear(),
      };
      const res = await apiService().post('fiscalPoliciesExt', policy);
      const newId = res?.data?.Id ?? res?.data?.id;

      if (newId) {
        for (const b of sortBrackets(brackets)) {
          await apiService().post('fiscalPoliticsDetails', {
            FiscalPoliticsId: newId,
            MinAmount:        b.From ?? 0,
            MaxAmount:        b.To,
            Percent:          b.Tax ?? 0,
            Description:      b.desc || '',
          });
        }
      }
      setSaved(true);
      setTimeout(() => history.push('/fiscal'), 1200);
    } catch {
      setErrors({ _general: 'Ruajtja dështoi. Provo sërisht.' });
    } finally {
      setSaving(false);
    }
  };

  const pensionTotal = Number(form.PensionEmployee || 0) + Number(form.PensionEmployer || 0);

  return (
    <Box>
      <PageHeader
        title="Politikë Fiskale e Re"
        actions={
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/fiscal')} variant="outlined">
            Kthehu
          </Button>
        }
      />

      {saved        && <Alert severity="success" sx={{ mb: 2 }}>U ruajt me sukses! Duke ridrejtuar…</Alert>}
      {toast        && <Alert severity="info"    sx={{ mb: 2 }}>{toast}</Alert>}
      {errors._general && <Alert severity="error" sx={{ mb: 2 }}>{errors._general}</Alert>}
      {errors._brackets && <Alert severity="error" sx={{ mb: 2 }}>{errors._brackets}</Alert>}

      {/* ── Seksioni 1: Informacioni i Politikës ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Informacioni i Politikës</Typography>
            <Button size="small" variant="outlined" color="info" onClick={loadKSDefaults}>
              Ngarko normat KS (default)
            </Button>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth required label="Emri i Politikës"
                value={form.Name} onChange={set('Name')}
                error={!!errors.Name} helperText={errors.Name}
                placeholder="p.sh. Politika Fiskale 2025"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth required type="date" label="Vlefshme nga"
                InputLabelProps={{ shrink: true }}
                value={form.ValidFrom} onChange={set('ValidFrom')}
                error={!!errors.ValidFrom} helperText={errors.ValidFrom}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="date" label="Vlefshme deri"
                InputLabelProps={{ shrink: true }}
                value={form.ValidTo} onChange={set('ValidTo')}
                helperText="Lëri bosh nëse politika nuk ka datë mbarimi"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.Active}
                    onChange={set('Active')}
                    color="primary"
                  />
                }
                label={form.Active ? <Chip size="small" label="Aktive" color="primary" /> : <Chip size="small" label="Joaktive" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Seksioni 2: Normat e Pensionit ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Normat e Pensionit (KBPK)</Typography>
            <Tooltip title="Sipas ligjit KS: 5% punëtor + 5% punëdhënës = 10% total. Këto norma zbatohen automatikisht në llogaritjen e listëpagesës dhe raporteve ATK/KBPK.">
              <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Stack>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth required type="number" label="Pensioni — Punëtor (%)"
                value={form.PensionEmployee} onChange={set('PensionEmployee')}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{ endAdornment: '%' }}
                error={!!errors.PensionEmployee} helperText={errors.PensionEmployee || 'Standard KS: 5%'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth required type="number" label="Pensioni — Punëdhënës (%)"
                value={form.PensionEmployer} onChange={set('PensionEmployer')}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{ endAdornment: '%' }}
                error={!!errors.PensionEmployer} helperText={errors.PensionEmployer || 'Standard KS: 5%'}
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 2, p: 2, textAlign: 'center', width: '100%' }}>
                <Typography variant="caption" color="primary.main" fontWeight={700}>TOTALI (punëtor + punëdhënës)</Typography>
                <Typography variant="h4" fontWeight={900} color="primary.main">{pensionTotal.toFixed(1)}%</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Seksioni 3: Kufijtë ligjorë ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Kufijtë Ligjorë (LP Kosovë)</Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Paga Minimale (€/muaj)"
                value={form.MinimumWage} onChange={set('MinimumWage')}
                inputProps={{ min: 0, step: 1 }}
                InputProps={{ endAdornment: '€' }}
                error={!!errors.MinimumWage} helperText={errors.MinimumWage || 'Standard KS 2024: 264€'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Orë Shtesë Maks/Javë"
                value={form.MaxOvertimeHoursWeek} onChange={set('MaxOvertimeHoursWeek')}
                inputProps={{ min: 0, max: 40, step: 1 }}
                InputProps={{ endAdornment: 'h/javë' }}
                helperText="LP Neni 23: maks 8 orë/javë"
              />
            </Grid>
          </Grid>

          {/* Normat e punës shtesë — LP Neni 56 */}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
            Normat e Pagës Shtesë — LP Neni 56 (konfigurueshme)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth type="number" label="Punë jashtë orarit (%)"
                value={form.OvertimeRate} onChange={set('OvertimeRate')}
                inputProps={{ min: 0, max: 200, step: 5 }}
                InputProps={{ endAdornment: '%' }}
                helperText="Min. ligjor: 30%"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth type="number" label="Punë nate (%)"
                value={form.NightWorkRate} onChange={set('NightWorkRate')}
                inputProps={{ min: 0, max: 200, step: 5 }}
                InputProps={{ endAdornment: '%' }}
                helperText="Min. ligjor: 30%"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth type="number" label="Fundjavë (%)"
                value={form.WeekendRate} onChange={set('WeekendRate')}
                inputProps={{ min: 0, max: 200, step: 5 }}
                InputProps={{ endAdornment: '%' }}
                helperText="Min. ligjor: 50%"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth type="number" label="Festa zyrtare (%)"
                value={form.HolidayRate} onChange={set('HolidayRate')}
                inputProps={{ min: 0, max: 200, step: 5 }}
                InputProps={{ endAdornment: '%' }}
                helperText="Min. ligjor: 50%"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth type="number" label="Kujdestari (%)"
                value={form.OnCallRate} onChange={set('OnCallRate')}
                inputProps={{ min: 0, max: 200, step: 5 }}
                InputProps={{ endAdornment: '%' }}
                helperText="Min. ligjor: 20%"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Seksioni 4: Shkallët Tatimore ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>Shkallët Tatimore (Tatimi mbi të Ardhurat)</Typography>
              <Tooltip title="Tatimi llogaritet progresivisht: secila shkallë aplikohet vetëm mbi shumën brenda intervalit.">
                <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Stack>
            <Button size="small" startIcon={<AddIcon />} onClick={addBracket} variant="outlined">
              Shto shkallë
            </Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Nr</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nga (€)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Deri (€)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tatimi (%)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Përshkrimi</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Veprime</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brackets.map((b, i) => (
                <TableRow key={b.id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    <TextField
                      size="small" type="number" value={b.From ?? ''}
                      onChange={e => setBracketField(b.id, 'From', e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{ width: 100 }}
                      InputProps={{ endAdornment: '€' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" type="number" value={b.To ?? ''}
                      onChange={e => setBracketField(b.id, 'To', e.target.value)}
                      placeholder="Pa kufi"
                      inputProps={{ min: 0 }}
                      sx={{ width: 110 }}
                      InputProps={{ endAdornment: '€' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" type="number" value={b.Tax ?? ''}
                      onChange={e => setBracketField(b.id, 'Tax', e.target.value)}
                      inputProps={{ min: 0, max: 100 }}
                      sx={{ width: 90 }}
                      InputProps={{ endAdornment: '%' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" value={b.desc ?? ''}
                      onChange={e => setBrackets(prev => prev.map(br => br.id === b.id ? { ...br, desc: e.target.value } : br))}
                      placeholder="p.sh. E liruar"
                      sx={{ width: 130 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Fshi shkallën">
                      <span>
                        <IconButton size="small" color="error" onClick={() => removeBracket(b.id)} disabled={brackets.length <= 1}>
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            <strong>Si funksionon tatimi progresiv:</strong> {describeBrackets(brackets, fmtEur)}
          </Typography>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || saved}
          size="large"
        >
          {saving ? 'Duke ruajtur…' : 'Ruaj Politikën'}
        </Button>
        <Button variant="outlined" onClick={() => history.push('/fiscal')} size="large">
          Anulo
        </Button>
      </Stack>
    </Box>
  );
}
